import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ANNOTATIONS_BASE_QUERY_KEY, createAnnotationAssignment } from 'api/annotationAssignments';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { AnnotationAssignment } from 'interfaces/annotation';
import { Permission } from 'interfaces/permissionOption';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getQueryErrorMessage } from 'utils/httpError';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';

import {
  ANALYSIS_AREAS_ASSIGNMENT_NAME,
  AnalysisAreaClassCounts,
  AnalysisAreaSlide,
  analysisAreaTodo,
  getAnalysisAreaSlideCounts,
  getAnalysisAreasDatasetUrl,
  hasDrawnAreas,
  isAnalysisAreaAssignment,
} from './analysisAreas';

interface AnalysisAreasDialogProps {
  studyId: string;
  labId?: string;
  slides: AnalysisAreaSlide[];
  onClose: () => void;
}

const formatAreaCount = (counts: AnalysisAreaClassCounts): string => {
  if (!hasDrawnAreas(counts)) {
    return 'None';
  }
  const parts = [];
  if (counts.saved > 0) {
    parts.push(`${counts.saved} saved`);
  }
  if (counts.draft > 0) {
    parts.push(`${counts.draft} draft`);
  }
  return parts.join(', ');
};

const AssignmentSlideTable = ({
  assignment,
  slides,
}: {
  assignment: AnnotationAssignment;
  slides: AnalysisAreaSlide[];
}) => {
  const annotationSlideIds = new Set((assignment.annotations || []).map((annotation) => annotation.slideId));
  const rows = [
    ...slides,
    ...(assignment.annotations || [])
      .filter((annotation) => !slides.some((slide) => slide.id === annotation.slideId))
      .map((annotation) => ({ id: annotation.slideId, label: annotation.slideId })),
  ];
  const counts = rows.map((slide) => getAnalysisAreaSlideCounts(assignment, slide.id));
  const includeSlides = counts.filter((item) => hasDrawnAreas(item.include)).length;
  const excludeSlides = counts.filter((item) => hasDrawnAreas(item.exclude)).length;
  const unchangedSlides = counts.filter((item) => !hasDrawnAreas(item.include) && !hasDrawnAreas(item.exclude)).length;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{assignment.name || ANALYSIS_AREAS_ASSIGNMENT_NAME}</Typography>
        <Chip size="small" variant="outlined" label={`Assignment ${assignment.annotationAssignmentId}`} />
        {assignment.archived ? <Chip size="small" label="Archived" /> : null}
        {annotationSlideIds.size === 0 ? <Chip size="small" variant="outlined" label="No saved areas yet" /> : null}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {`${includeSlides} ${includeSlides === 1 ? 'slide has' : 'slides have'} Include areas. ${excludeSlides} ${
          excludeSlides === 1 ? 'slide has' : 'slides have'
        } Exclude areas. ${unchangedSlides} ${unchangedSlides === 1 ? 'slide is' : 'slides are'} unchanged.`}
      </Typography>
      <Table size="small" aria-label={`${assignment.name || 'Analysis areas'} by slide`}>
        <TableHead>
          <TableRow>
            <TableCell>Slide</TableCell>
            <TableCell>Include</TableCell>
            <TableCell>Exclude</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((slide) => {
            const slideCounts = getAnalysisAreaSlideCounts(assignment, slide.id);
            return (
              <TableRow key={`${assignment.annotationAssignmentId}-${slide.id}`}>
                <TableCell>{slide.label}</TableCell>
                <TableCell>{formatAreaCount(slideCounts.include)}</TableCell>
                <TableCell>{formatAreaCount(slideCounts.exclude)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
};

export const AnalysisAreasDialog: React.FC<AnalysisAreasDialogProps> = ({ studyId, labId, slides, onClose }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(Permission.CreateAnnotationAssignment);
  const [createError, setCreateError] = useState<string | null>(null);
  const assignmentsQuery = useAnnotationAssignments(studyId, false, true);
  const assignments = (assignmentsQuery.data?.annotationAssignments || []).filter(isAnalysisAreaAssignment);
  const activeAssignments = assignments.filter((assignment) => !assignment.archived);
  const exploreAssignment = activeAssignments.length === 1 ? activeAssignments[0] : undefined;
  const exploreDatasetUrl =
    exploreAssignment && slides.length
      ? getAnalysisAreasDatasetUrl({
          studyId,
          labId,
          slideIds: slides.map((slide) => slide.id),
          annotationAssignmentId: exploreAssignment.annotationAssignmentId,
        })
      : undefined;
  const canCreateAssignment = canCreate && activeAssignments.length === 0 && slides.length > 0;

  const createAssignment = useMutationWithErrorSnackbar({
    mutationDescription: 'Create analysis-area assignment',
    mutationFn: createAnnotationAssignment,
    onError: (error) => {
      setCreateError(getQueryErrorMessage(error, 'The analysis-area assignment was not created.'));
    },
    onSuccess: async () => {
      setCreateError(null);
      await queryClient.invalidateQueries([ANNOTATIONS_BASE_QUERY_KEY]);
    },
  });

  const createAnalysisAreas = () => {
    setCreateError(null);
    createAssignment.mutate({
      name: ANALYSIS_AREAS_ASSIGNMENT_NAME,
      isGlobal: false,
      todos: [analysisAreaTodo()],
      labId,
      studyId,
      filters: { studyId },
      slideIdsToInclude: slides.map((slide) => slide.id),
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="analysis-areas-title">
      <DialogChromeHeader
        title="Analysis areas"
        titleId="analysis-areas-title"
        onClose={onClose}
        subtitle="Draw Include within the tissue mask to limit the analysis area. Without Include, the tissue mask is used. Exclude areas are then removed. Cells outside the resulting area are removed."
      />
      <DialogContent dividers>
        {assignmentsQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} aria-label="Loading analysis areas" />
            <Typography variant="body2" color="text.secondary">
              Loading analysis areas…
            </Typography>
          </Stack>
        ) : assignmentsQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" onClick={() => assignmentsQuery.refetch()}>
                Retry
              </Button>
            }
          >
            Analysis areas could not be loaded.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {assignments.length > 1 ? (
              <Alert severity="info">
                More than one analysis-area assignment exists. Each one stays listed. A new assignment is not created
                while an active one is already present.
              </Alert>
            ) : null}
            {createError ? <Alert severity="error">{createError}</Alert> : null}
            {assignments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {!canCreate
                  ? 'Creating an analysis-area assignment requires annotation assignment permission.'
                  : slides.length
                  ? `Create one study assignment for ${slides.length} multiplex ${
                      slides.length === 1 ? 'slide' : 'slides'
                    }. Include focuses analysis. Exclude leaves areas out.`
                  : 'No multiplex slides are in this study yet.'}
              </Typography>
            ) : (
              assignments.map((assignment) => (
                <Stack key={assignment.annotationAssignmentId} spacing={1}>
                  {assignments.length > 1 ? <Chip size="small" variant="outlined" label="Duplicate" /> : null}
                  <AssignmentSlideTable assignment={assignment} slides={slides} />
                </Stack>
              ))
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogChromeFooter>
        {exploreDatasetUrl ? (
          <Button component={RouterLink} to={exploreDatasetUrl} variant="outlined" color="secondary">
            Explore dataset and annotate areas
          </Button>
        ) : null}
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
        {canCreate && activeAssignments.length === 0 && !assignmentsQuery.isLoading && !assignmentsQuery.isError ? (
          <Button
            variant="contained"
            color="primary"
            onClick={createAnalysisAreas}
            disabled={!canCreateAssignment || createAssignment.isLoading}
          >
            {createAssignment.isLoading ? 'Creating analysis-area assignment' : 'Create analysis-area assignment'}
          </Button>
        ) : null}
      </DialogChromeFooter>
    </Dialog>
  );
};
